import { ComponentType, useMemo } from 'react';
import {
  ChartSeries,
  foldBinSeries,
  MetricData,
  parseChartData,
  RawSeries,
  RawTab,
  TabItem,
  toAxisSeries,
  toTabItems,
  toTableSeries,
  toValueSeries,
} from 'helpers';
import { BubbleChart } from './BubbleChart';
import styles from './ChartRenderer.module.css';
import { HorizontalBarChart } from './HorizontalBarChart';
import { LineChart } from './LineChart';
import { PieChart } from './PieChart';
import { ScatterChart } from './ScatterChart';
import { TableChart } from './TableChart';
import { TableChartBar } from './TableChartBar';
import { TabView } from './TabView';
import { VerticalBarChart } from './VerticalBarChart';

type ChartInput = RawSeries[] | ChartSeries[] | RawTab[] | MetricData | string;

interface ChartRendererProps {
  chartType: string;
  chartData?: ChartInput;
  /** chartData is already the series the dashboard draws (what the backend returns), so it is not converted from the query editor's raw rows. */
  prepared?: boolean;
}

interface SeriesChartProps {
  chartData: ChartSeries[];
}

interface SeriesChartConfig {
  Chart: ComponentType<SeriesChartProps>;
  process: (data: RawSeries[]) => ChartSeries[];
  fullWidth?: boolean;
}

const StackedBarChart = ({ chartData }: SeriesChartProps) => <VerticalBarChart chartData={chartData} stacked />;
const DonutChart = ({ chartData }: SeriesChartProps) => <PieChart chartData={chartData} donut />;

const SERIES_CHARTS: Record<string, SeriesChartConfig> = {
  bar: { Chart: VerticalBarChart, process: (d) => toValueSeries(d) },
  horizontalBar: { Chart: HorizontalBarChart, process: (d) => toValueSeries(d) },
  // Several series side by side, piled up, or binned: the same bars with another layout.
  multibar: { Chart: VerticalBarChart, process: (d) => toValueSeries(d) },
  histogram: { Chart: VerticalBarChart, process: (d) => toValueSeries(d) },
  stackedbar: { Chart: StackedBarChart, process: (d) => toValueSeries(d) },
  donut: { Chart: DonutChart, process: (d) => toValueSeries(d, { includeColor: true }) },
  multiline: { Chart: LineChart, process: (d) => toValueSeries(d, { vizType: 'line' }) },
  // No chart.js heatmap is installed yet, so it is shown as the table until the Architect approves a matrix plugin (CODING_STANDARDS 2.7).
  heatmap: { Chart: TableChart, process: toTableSeries, fullWidth: true },
  // A funnel is stages from widest to narrowest: ranked horizontal bars.
  funnel: { Chart: HorizontalBarChart, process: (d) => toValueSeries(d) },
  conversionFunnel: { Chart: HorizontalBarChart, process: (d) => toValueSeries(d) },
  stageFunnel: { Chart: HorizontalBarChart, process: (d) => toValueSeries(d) },
  pie: { Chart: PieChart, process: (d) => toValueSeries(d, { includeColor: true }) },
  line: { Chart: LineChart, process: (d) => toValueSeries(d, { vizType: 'line' }) },
  bubble: { Chart: BubbleChart, process: (d) => toAxisSeries(d, 'bubble') },
  scatter: { Chart: ScatterChart, process: (d) => toAxisSeries(d, 'scatter') },
  table: { Chart: TableChart, process: toTableSeries, fullWidth: true },
  tableBar: { Chart: TableChartBar, process: toTableSeries, fullWidth: true },
};

const asArray = <T,>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);

interface ChartFrameProps {
  fullWidth?: boolean;
  children: React.ReactNode;
}

const ChartFrame = ({ fullWidth, children }: ChartFrameProps) => (
  <div className={`${styles.chart_renderer} p-2${fullWidth ? ' col-12' : ''}`}>
    <div className="">{children}</div>
  </div>
);

interface MetricViewProps {
  metric?: MetricData;
}

const MetricView = ({ metric }: MetricViewProps) => (
  <div>
    <p className={`${styles.metric_title}`}>{`${metric?.title || 'Metric'} Count`}</p>
    <p className={`${styles.large_number}`}>{metric?.count}</p>
    {metric?.insight && <p className="small text-secondary">{metric.insight}</p>}
  </div>
);

export const ChartRenderer = ({ chartType, chartData, prepared = false }: ChartRendererProps) => {
  const parsed = useMemo(() => parseChartData<ChartInput>(chartData), [chartData]);
  const seriesChart = SERIES_CHARTS[chartType];

  const series = useMemo(
    () => {
      if (!seriesChart) return [];
      if (!prepared) return seriesChart.process(asArray<RawSeries>(parsed));
      return chartType === 'histogram' ? foldBinSeries(asArray<ChartSeries>(parsed)) : asArray<ChartSeries>(parsed);
    },
    [seriesChart, parsed, prepared, chartType]
  );
  const tabs = useMemo<TabItem[]>(
    () => (chartType === 'tabView' ? toTabItems(asArray<RawTab>(parsed)) : []),
    [chartType, parsed]
  );

  if (seriesChart) {
    const { Chart, fullWidth } = seriesChart;
    return (
      <ChartFrame fullWidth={fullWidth}>
        <Chart chartData={series} />
      </ChartFrame>
    );
  }

  if (chartType === 'tabView') {
    return (
      <ChartFrame fullWidth>
        <TabView tabData={tabs} />
      </ChartFrame>
    );
  }

  if (chartType === 'metric') return <MetricView metric={parsed as MetricData | undefined} />;

  return null;
};
